/**
 * @fileoverview Canonical reserved first-label hostnames for the tenant domain
 *             base, split by the two policies that consume them:
 *             infrastructure endpoints that must never resolve to a tenant,
 *             and the wider mint/client-reserved set that can never be a
 *             department slug.
 * @module sync-config/reserved-host-labels
 *
 * Responsibilities:
 * - Define INFRA_HOST_LABELS: labels routed by Front Door to services or env
 *   apexes — host resolution must never treat them as tenant slugs.
 * - Define RESERVED_HOST_LABELS: the full set (infra + client-reserved routing
 *   labels) that must never be minted as a department slug and never resolved
 *   by the web clients.
 *
 * Dependencies:
 * - None (pure constants).
 *
 * Architecture:
 * - Single source of truth: tenant-api and mobile-api host extraction use
 *   INFRA_HOST_LABELS (an existing department that owns a client-reserved
 *   label like `admin` keeps host routing — refusing it would strand its
 *   subdomain), while system-api's `assertTenantIdentityAvailable`,
 *   tenant-web `resolveSlug`, and mobile-pwa `resolveTenantSlug` use the
 *   full RESERVED_HOST_LABELS union. Per-host copies drift and strand
 *   tenants whose slug one layer accepts and another rejects.
 *
 * Notes:
 * - Infra membership: Front Door service endpoints and env apex/web hosts in
 *   `infra/azure-shared/front-door.tf`. Client-reserved membership: labels
 *   the web clients refuse to treat as tenant subdomains (admin, www, auth,
 *   api, demo).
 * - When Front Door gains an endpoint add it to INFRA_HOST_LABELS; when a
 *   client reserves a new label add it to RESERVED_HOST_LABELS only.
 */

/**
 * @contract INFRA_HOST_LABELS
 *
 * Purpose:
 * - First labels that are infrastructure endpoints or env apex/web hosts —
 *   never tenant subdomains at resolution time.
 *
 * Fields:
 * - Lowercase first-label strings.
 *
 * Rules:
 * - Host extractors return undefined for these labels instead of a slug.
 * - Compare against a normalized (lowercase, port-stripped) first label.
 *
 * Sync:
 * - Shared by api and web hosts via `@firesquirrel/sync-config`.
 */
export const INFRA_HOST_LABELS = new Set([
  'tenant-api',
  'system-api',
  'mobile-api',
  'system-dev',
  'system-stage',
  'system',
  'dev',
  'stage',
  'prod',
]);

/**
 * @contract RESERVED_HOST_LABELS
 *
 * Purpose:
 * - Labels that can never be minted as a department slug and that the web
 *   clients never resolve as tenant subdomains (infra labels plus
 *   client-reserved routing labels).
 *
 * Fields:
 * - Lowercase first-label strings; superset of INFRA_HOST_LABELS.
 *
 * Rules:
 * - The mint guard rejects these for NEW claims only — a department created
 *   before a label was reserved keeps its slug.
 * - Client resolvers return null for these labels.
 *
 * Sync:
 * - Shared by api and web hosts via `@firesquirrel/sync-config`.
 */
export const RESERVED_HOST_LABELS = new Set([
  ...INFRA_HOST_LABELS,
  'admin',
  'www',
  'auth',
  'api',
  'demo',
]);

/**
 * @process extractTenantSlugFromHost
 *
 * Steps:
 * 1. Strip the port and lowercase the hostname.
 * 2. Require the host to end with `.{tenantDomainBase}`.
 * 3. Take the first label of the remaining prefix as the slug.
 * 4. Return undefined for INFRA labels (service endpoints/env apexes are not
 *    tenant subdomains) and empty slugs.
 *
 * Inputs:
 * - hostname: Request host (e.g. `pfd.dev.getdefensible.app`).
 * - tenantDomainBase: configured tenant domain base (e.g. `getdefensible.app`).
 *
 * Outputs:
 * - Lowercase tenant slug, or undefined when the host is not a tenant
 *   subdomain.
 *
 * Failure Handling:
 * - Missing/foreign/reserved hosts return undefined.
 *
 * Idempotency:
 * - REQUIRED — pure classification.
 */
export function extractTenantSlugFromHost(hostname: string | undefined, tenantDomainBase: string): string | undefined {
  if (!hostname) return undefined;

  const base = tenantDomainBase.toLowerCase();
  const host = hostname.toLowerCase().split(':')[0];

  // Pattern: {slug}.{env}.{domainBase} or {slug}.{domainBase}
  if (!host.endsWith(`.${base}`)) return undefined;

  const prefix = host.slice(0, -(base.length + 1));
  if (!prefix) return undefined;

  const slug = prefix.split('.')[0];
  if (INFRA_HOST_LABELS.has(slug)) return undefined;
  return slug || undefined;
}
